import React, { useState, useCallback } from 'react';
import { DeepSyncSpinner } from '@/ui/brand/DeepSyncSpinner';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { X, FileArchive } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { useKmlDialog } from '@/contexts/KmlDialogContext';
import { useTheme } from '@/contexts/ThemeContext';

const ALLOWED_EXT = ['.kml', '.kmz'];
const MAX_SIZE = 50 * 1024 * 1024;

function isValidFile(f: File) {
  const name = f.name.toLowerCase();
  return ALLOWED_EXT.some((ext) => name.endsWith(ext));
}

export default function KmlToShapefileDialog() {
  const { open, closeDialog } = useKmlDialog();
  const { getIdToken } = useAuth();
  const { mode } = useTheme();
  const [queue, setQueue] = useState<File[]>([]);
  const [status, setStatus] = useState<'idle' | 'converting' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [successCount, setSuccessCount] = useState(0);

  const addFiles = useCallback((files: FileList | File[]) => {
    const arr = Array.from(files);
    const valid = arr.filter((f) => {
      if (!isValidFile(f)) return false;
      if (f.size > MAX_SIZE) return false;
      return true;
    });
    setQueue((prev) => {
      const names = new Set(prev.map((p) => p.name));
      const newOnes = valid.filter((f) => !names.has(f.name));
      return [...prev, ...newOnes];
    });
    setError(null);
    setStatus('idle');
  }, []);

  const removeFile = (index: number) => {
    setQueue((prev) => prev.filter((_, i) => i !== index));
    setError(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files?.length) addFiles(files);
    e.target.value = '';
  };

  const convertOne = async (file: File): Promise<boolean> => {
    const formData = new FormData();
    formData.append('file', file);
    const token = await getIdToken();
    const apiUrl = import.meta.env.VITE_API_URL || 'https://aerial-backend-415603271201.us-west1.run.app/api';
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      headers['X-Firebase-Auth'] = `Bearer ${token}`;
    }

    const res = await fetch(`${apiUrl}/tools/kml-to-shapefile`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!res.ok) {
      const text = await res.text();
      let errMsg = `Conversion failed (${res.status})`;
      try {
        const errData = JSON.parse(text);
        errMsg = errData.detail || errMsg;
      } catch {
        if (text) errMsg = text.slice(0, 200);
      }
      throw new Error(errMsg);
    }

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${file.name.replace(/\.(kml|kmz)$/i, '')}_shapefiles.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return true;
  };

  const handleConvert = async () => {
    if (queue.length === 0) return;

    setStatus('converting');
    setError(null);
    setSuccessCount(0);

    let ok = 0;
    for (let i = 0; i < queue.length; i++) {
      try {
        await convertOne(queue[i]);
        ok++;
        setSuccessCount(ok);
      } catch (e: any) {
        setError(e?.message || 'Conversion failed');
        setStatus('error');
        return;
      }
    }

    setStatus('success');
  };

  const handleClose = () => {
    if (status !== 'converting') {
      setQueue([]);
      setStatus('idle');
      setError(null);
      setSuccessCount(0);
      closeDialog();
    }
  };

  const isDark = mode === 'dark';
  const paperBg = isDark ? 'rgba(30, 41, 59, 1)' : 'rgba(255, 255, 255, 1)';
  const borderColor = isDark ? 'rgba(51, 65, 85, 0.3)' : 'rgba(0,0,0,0.08)';
  const dropZoneBg = isDark ? 'rgba(51, 65, 85, 0.4)' : 'rgba(0,0,0,0.04)';
  const dropZoneHoverBg = isDark ? 'rgba(51, 65, 85, 0.6)' : 'rgba(0,0,0,0.06)';
  const textPrimary = isDark ? '#f8fafc' : '#0f172a';
  const textSecondary = isDark ? '#94a3b8' : '#64748b';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: isDark ? 'rgba(0, 0, 0, 0.7) !important' : 'rgba(0, 0, 0, 0.5) !important',
        },
      }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: `${paperBg} !important`,
            background: `${paperBg} !important`,
            border: `1px solid ${borderColor}`,
            boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.12)',
          },
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          px: 2,
          borderBottom: 1,
          borderColor,
          bgcolor: paperBg,
          color: textPrimary,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          KML to Shapefile
        </Typography>
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={status === 'converting'}
          aria-label="Close"
          sx={{ color: textSecondary }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2, bgcolor: paperBg, color: textPrimary }}>
        <Typography variant="body2" sx={{ mb: 2, color: textSecondary }}>
          Drag and drop KML or KMZ files here, or click to browse. Max 50MB per file.
        </Typography>

        {/* Drop zone */}
        <Box
          component="label"
          htmlFor="kml-dialog-file-input"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          sx={{
            display: 'block',
            p: 3,
            mb: 2,
            border: '2px dashed',
            borderColor,
            borderRadius: 2,
            bgcolor: dropZoneBg,
            cursor: 'pointer',
            transition: 'all 0.2s',
            textAlign: 'center',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: dropZoneHoverBg,
            },
          }}
        >
          <input
            accept=".kml,.kmz"
            id="kml-dialog-file-input"
            type="file"
            multiple
            onChange={handleFileInput}
            style={{ display: 'none' }}
          />
          <FileArchive size={36} style={{ marginBottom: 8, opacity: 0.5 }} />
          <Typography variant="body2" sx={{ color: textSecondary }}>
            Drop files here or click to browse
          </Typography>
        </Box>

        {/* Queue */}
        {queue.length > 0 && (
          <Box sx={{ mb: 2, maxHeight: 180, overflow: 'auto', border: 1, borderColor, borderRadius: 1 }}>
            <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor, bgcolor: dropZoneBg }}>
              <Typography variant="caption" fontWeight={600} sx={{ color: textSecondary }}>
                {queue.length} file{queue.length !== 1 ? 's' : ''} ready
              </Typography>
            </Box>
            <List dense disablePadding>
              {queue.map((f, i) => (
                <ListItem
                  key={`${f.name}-${i}`}
                  secondaryAction={
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={() => removeFile(i)}
                      disabled={status === 'converting'}
                      aria-label="Remove"
                      sx={{ color: textSecondary }}
                    >
                      <X size={14} />
                    </IconButton>
                  }
                  sx={{ py: 0.5, color: textPrimary }}
                >
                  <ListItemText
                    primary={f.name}
                    secondary={`${(f.size / 1024).toFixed(1)} KB`}
                    slotProps={{
                      primary: { variant: 'body2' },
                      secondary: { variant: 'caption', sx: { color: textSecondary } }
                    }} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Convert button */}
        <Button
          variant="contained"
          fullWidth
          disabled={queue.length === 0 || status === 'converting'}
          startIcon={
            status === 'converting' ? (
              <DeepSyncSpinner size={18} color="inherit" />
            ) : null
          }
          onClick={handleConvert}
          sx={{ py: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          {status === 'converting'
            ? `Converting ${successCount}/${queue.length}...`
            : 'Convert to Shapefile'}
        </Button>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {status === 'success' && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Converted {queue.length} file{queue.length > 1 ? 's' : ''}. Check your downloads.
          </Alert>
        )}
      </DialogContent>
    </Dialog>
  );
}
